import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { reaction } from 'mobx'
import i from '../../../translations/i'
import __order, { OrdersSort } from './__order'
import { paymentTypesController } from '../payment/types/controller'
import { Titles } from '../../../layouts/Titles'
import {
    Alert,
    Button,
    Checkbox,
    DatePicker,
    Dropdown,
    Input,
    InputNumber,
    Segmented,
    Select,
    Switch,
    Tooltip,
} from 'antd'
import type { MenuProps } from 'antd'
import {
    CalculatorOutlined,
    CarOutlined,
    CreditCardOutlined,
    EnvironmentOutlined,
    GatewayOutlined,
    ExportOutlined,
    EyeOutlined,
    GiftOutlined,
    HomeOutlined,
    InboxOutlined,
    MailOutlined,
    MessageOutlined,
    MoreOutlined,
    NumberOutlined,
    PhoneOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    SearchOutlined,
    ShopOutlined,
    ShoppingOutlined,
    UserOutlined,
    WalletOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import __multiple from '../../../helpers/multiple_actions/__multiple'
import { WarehousesController } from '../../settings/warehouses/warehouses.controller'
import { getPrice } from '../../../helpers/price'
import { storeLocal } from '../../../helpers/stores'
import {
    CardList,
    cleanText,
    EMPTY_VALUE,
    ListCard,
    ListChip,
    ListMetricProps,
    ListToolbar,
    ListToolbarBreak,
    ListToolbarChips,
    ListToolbarField,
    ListToolbarSpacer,
} from '../../../components/card_list/CardList'
import './orders_list.less'

const payments = new paymentTypesController()
const warehouses = new WarehousesController()

// Статус оплаты «не оплачен» — тот же id, что у бэкенда (OrdersOpenApi::getInfoNotPaymentOrders)
const UNPAID = 1
const PAID = 2

const locale = () => {
    const lang = storeLocal.get('lang') || process.env.REACT_APP_DEFAULT_LANGUAGE || 'en'
    return lang === 'ua' ? 'uk' : lang
}

const sameYear = (date: Dayjs) => date.year() === dayjs().year()

// Создание — момент в UTC, показываем в поясе менеджера
const formatCreated = (iso: string | null) => {
    if (!iso) return null
    const date = dayjs(iso)
    return new Intl.DateTimeFormat(locale(), {
        day: 'numeric',
        month: 'short',
        year: sameYear(date) ? undefined : 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date.toDate())
}

// «Сегодня», «Завтра», «Вчера» или «21 окт.»
const dayLabel = (date: Dayjs) => {
    const diff = date.startOf('day').diff(dayjs().startOf('day'), 'day')
    if (diff === 0) return i.t('orders:list.today')
    if (diff === 1) return i.t('orders:list.tomorrow')
    if (diff === -1) return i.t('orders:list.yesterday')
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: sameYear(date) ? undefined : 'numeric' }).format(date.toDate())
}

const hhmm = (seconds: number) => `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}`

// Российские номера показываем как +7 999 123-45-67, остальные — как есть
const formatPhone = (phone: string | null) => {
    const raw = cleanText(phone)
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8'))
        return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    return raw
}

const shorten = (text: string, max: number) => text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text

const sourceLabel = (source: string | null) => {
    if (source === 'ios') return 'iOS'
    if (source === 'android') return 'Android'
    if (source === 'web') return i.t('order:site')
    return null
}

const statusColor = (status: any) => status?.color_hex ? `#${status.color_hex}` : 'var(--text-muted-color)'

const StatusDot = ({ color }: { color: string }) => <span className="ol-dot" style={{ background: color }} />

const StatusBadge = ({ status }: { status: any }) => (
    <span className="ol-status" style={{ '--ol-status': statusColor(status) } as React.CSSProperties}>
        <StatusDot color={statusColor(status)} />
        {status.name}
    </span>
)

// Дата и интервал доставки. delivery_date — время склада без пояса, поэтому читаем его как есть
const deliveryMetric = (order: any): ListMetricProps => {
    const pickup = Number(order.delivery_type) === 2
    const type = pickup ? i.t('order:pickup') : i.t('order:deliveries')
    const date = order.delivery_date ? dayjs(order.delivery_date) : null

    let time: string | null = null
    if (order.delivery_interval_express) time = i.t('orders:list.express')
    else if (order.delivery_interval_start || order.delivery_interval_end) time = `${hhmm(order.delivery_interval_start ?? 0)}–${hhmm(order.delivery_interval_end ?? 0)}`

    const today = !!date && date.isSame(dayjs(), 'day')

    return {
        value: date && date.isValid() ? dayLabel(date) : EMPTY_VALUE,
        // С интервалом тип доставки — иконкой, иначе подпись не помещается в ячейку
        label: time ? <span className="ol-delivery">{pickup ? <ShopOutlined /> : <CarOutlined />}{time}</span> : type,
        hint: date && date.isValid()
            ? `${type}: ${new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', year: 'numeric' }).format(date.toDate())}${time ? `, ${time}` : ''}`
            : undefined,
        tone: today ? 'primary' : undefined,
    }
}

const OrderCard = observer(({ order }: { order: any }) => {
    const status = __order.statuses.find((el: any) => el.id === order.status)
    const payment = payments.list.find((el: any) => `${el.id}` === `${order.payment_status}`)
    const warehouse = order.warehouse_id > 0 ? warehouses.list.find((el: any) => el.id === order.warehouse_id) : null
    const zone = order.delivery_zone_id > 0 ? warehouses.zones.find((el: any) => el.id === order.delivery_zone_id) : null

    const name = [order.firstname, order.lastname].map(cleanText).filter(Boolean).join(' ')
    const phone = formatPhone(order.phone)
    const email = cleanText(order.email)
    const pickup = Number(order.delivery_type) === 2
    // Самовывоз из пункта выдачи ТК: филиала нет, есть пункт выдачи из интеграции
    const address = pickup
        ? cleanText(order.pickup_point_name) || cleanText(order.place?.name) || cleanText(order.place?.address) || cleanText(order.pickup_point_address) || cleanText(order.address)
        : cleanText(order.address) || cleanText(order.address_full)
    const addressHint = pickup
        ? cleanText(order.pickup_point_address) || cleanText(order.place?.address) || address
        : cleanText(order.address_full) || address
    const service = order.delivery && order.delivery !== 'default' ? __order.getDeliveryNameByType(order.delivery) : ''
    const transportCompany = cleanText(order.delivery_method_name) || (cleanText(order.delivery_method_id) ? `#${cleanText(order.delivery_method_id)}` : '')
    // Оплата после сборки: карта привязана к заказу, сумму спишут с неё после сборки
    const savedCard = Number(order.save_card_id) > 0
    const savedCardLabel = __order.savedCardLabel(order.saved_card)
    const comment = cleanText(order.comment)
    const recipient = order.recipient ? [cleanText(order.recipient_name), formatPhone(order.recipient_phone)].filter(Boolean).join(', ') : ''
    const source = sourceLabel(order.source)
    const selected = __multiple.selected.includes(order.id)
    const href = `/shop/orders/${order.id}`

    const toggleSelected = (checked: boolean) => {
        const ids = checked ? [...__multiple.selected, order.id] : __multiple.selected.filter(id => id !== order.id)
        __multiple.set(__order.list.filter((el: any) => ids.includes(el.id)))
    }

    const menu: MenuProps['items'] = [
        { key: 'open', icon: <ExportOutlined />, label: i.t('orders:list.openNewTab'), onClick: () => window.open(href, '_blank') },
        ...(!order.viewed ? [{ key: 'viewed', icon: <EyeOutlined />, label: i.t('orders:list.markViewed'), onClick: () => __order.markViewedFromList(order.id) }] : []),
        {
            key: 'archive',
            icon: <InboxOutlined />,
            label: order.is_archive ? i.t('def:fromArchive') : i.t('def:toArchive'),
            onClick: () => __order.setArchivedFromList(order.id, !order.is_archive),
        },
    ]

    return (
        <ListCard
            href={href}
            selected={selected}
            // Отменённые видны, но не спорят с живыми
            muted={!!status?.is_canceled}
            // Новый заказ, который ещё никто не открыл, должен бросаться в глаза
            accent={!order.viewed}
            title={<>
                {i.t('orders:list.order', { id: order.id })}
                {cleanText(order.external_number) && <span className="ol-external"> · {cleanText(order.external_number)}</span>}
            </>}
            titleExtra={<>
                {status && <StatusBadge status={status} />}
                <ListChip icon={pickup ? <ShopOutlined /> : <CarOutlined />}>{pickup ? i.t('order:pickup') : i.t('order:deliveries')}</ListChip>
                {!!order.is_archive && !__order.pagination.is_archive && <ListChip icon={<InboxOutlined />}>{i.t('orders:list.archived')}</ListChip>}
            </>}
            subtitle={[
                i.t('orders:list.created', { date: formatCreated(order.created_at) }),
                source,
                order.product_count > 0 ? i.t('orders:list.items', { count: order.product_count }) : null,
            ].filter(Boolean).join(' · ')}
            chips={<>
                {order.user_id > 0
                    ? <a href={`/users/${order.user_id}`} className="ol-chip-link"><ListChip icon={<UserOutlined />} tooltip={i.t('orders:list.clientCard')}>{name || i.t('orders:list.client', { id: order.user_id })}</ListChip></a>
                    : name && <ListChip icon={<UserOutlined />}>{name}</ListChip>
                }
                {phone && <ListChip icon={<PhoneOutlined />} copy={phone}>{phone}</ListChip>}
                {!phone && email && <ListChip icon={<MailOutlined />} copy={email}>{email}</ListChip>}
                {address &&
                    <ListChip
                        icon={pickup ? <ShopOutlined /> : <EnvironmentOutlined />}
                        tooltip={addressHint}
                    >
                        {shorten(address, 48)}
                    </ListChip>
                }
                {service && <ListChip icon={<CarOutlined />}>{service}</ListChip>}
                {transportCompany &&
                    <ListChip
                        icon={<CarOutlined />}
                        tone="info"
                        tooltip={[i.t('order:tk'), cleanText(order.delivery_method_description)].filter(Boolean).join(' · ')}
                    >
                        {transportCompany}
                    </ListChip>
                }
                {savedCard &&
                    <ListChip
                        icon={<CreditCardOutlined />}
                        tone="success"
                        tooltip={[savedCardLabel, i.t('order:savedCardHint')].filter(Boolean).join(' — ')}
                    >
                        {i.t('order:savedCardAttached')}
                    </ListChip>
                }
                {warehouse && <ListChip icon={<HomeOutlined />} tooltip={i.t('def:warehouse')}>{warehouse.name}</ListChip>}
                {zone && <ListChip icon={<GatewayOutlined />} tooltip={i.t('orders:filters.zone')}>{zone.name}</ListChip>}
                {recipient && <ListChip icon={<UserOutlined />} tooltip={i.t('orders:list.recipient')}>{recipient}</ListChip>}
                {order.size_bonus_points > 0 &&
                    <ListChip icon={<GiftOutlined />}>{i.t('orders:list.bonusesWritten', { value: Number(order.size_bonus_points).toLocaleString() })}</ListChip>
                }
                {comment && <ListChip icon={<MessageOutlined />} tooltip={comment}>{shorten(comment, 40)}</ListChip>}
            </>}
            metricWidth={132}
            metrics={[
                { value: getPrice(Number(order.sum ?? 0).toFixed(2)), label: i.t('orders:list.sum') },
                {
                    value: payment?.name ?? EMPTY_VALUE,
                    label: i.t('orders:list.payment'),
                    hint: payment?.name,
                    tone: Number(order.payment_status) === PAID ? 'success' : undefined,
                },
                deliveryMetric(order),
            ]}
            actions={<>
                <Tooltip title={i.t('orders:list.select')}>
                    <Checkbox checked={selected} onChange={e => toggleSelected(e.target.checked)} aria-label={i.t('orders:list.select')} />
                </Tooltip>
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <Button type="text" shape="circle" icon={<MoreOutlined />} aria-label={i.t('def:actions.actions')} />
                </Dropdown>
            </>}
        />
    )
})

const SummaryTile = ({ icon, tone, label, value, hint }: {
    icon: React.ReactNode
    tone: string
    label: React.ReactNode
    value: React.ReactNode
    hint?: string
}) => (
    <div className="ol-tile">
        <span className={`ol-tile__icon ol-tone--${tone}`}>{icon}</span>
        <div className="ol-tile__body">
            <div className="ol-tile__value" title={typeof value === 'string' ? value : undefined}>{value}</div>
            <div className="ol-tile__label">
                {label}
                {hint && <Tooltip title={hint}><QuestionCircleOutlined className="ol-hint" /></Tooltip>}
            </div>
        </div>
    </div>
)

// Итоги по всем заказам под фильтрами, не только по странице
const OrdersSummary = observer(() => {
    const { total, sum, bonuses } = __order.pagination
    const counting = __order.filterOrders.filters.bonuses
    const ready = __order.isLoading.list

    return (
        <section className={`ol-summary${__order.listLoading ? ' ol-summary--loading' : ''}`}>
            <div className="ol-summary__tiles">
                <SummaryTile icon={<ShoppingOutlined />} tone="violet" label={i.t('orders:summary.orders')} value={ready ? Number(total).toLocaleString() : EMPTY_VALUE} />
                <SummaryTile icon={<WalletOutlined />} tone="orange" label={i.t('orders:summary.sum')} value={ready ? getPrice(Number(sum ?? 0).toFixed(2)) : EMPTY_VALUE} />
                <SummaryTile
                    icon={<CalculatorOutlined />}
                    tone="cyan"
                    label={i.t('orders:summary.avg')}
                    value={ready && total > 0 ? getPrice((sum / total).toFixed(2)) : EMPTY_VALUE}
                />
                <div className={`ol-tile ol-tile--bonuses${counting ? '' : ' ol-tile--off'}`}>
                    <span className="ol-tile__icon ol-tone--green"><GiftOutlined /></span>
                    <div className="ol-tile__body">
                        {counting && bonuses
                            ? <div className="ol-tile__pair">
                                <span><b>+{Number(bonuses.accrued ?? 0).toLocaleString()}</b> {i.t('orders:summary.accrued')}</span>
                                <span><b>−{Number(bonuses.written ?? 0).toLocaleString()}</b> {i.t('orders:summary.written')}</span>
                            </div>
                            : <div className="ol-tile__value ol-tile__value--muted">{counting ? EMPTY_VALUE : i.t('orders:summary.bonusesOff')}</div>
                        }
                        <label className="ol-tile__label ol-tile__switch">
                            <Switch size="small" checked={counting} onChange={value => __order.setFilters({ bonuses: value })} />
                            {i.t('orders:summary.bonuses')}
                            <Tooltip title={i.t('orders:summary.bonusesHint')}><QuestionCircleOutlined className="ol-hint" /></Tooltip>
                        </label>
                    </div>
                </div>
            </div>
        </section>
    )
})

const today = () => dayjs().startOf('day')

const createdPresets = () => [
    { label: i.t('orders:filters.presets.today'), value: [today(), today()] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.yesterday'), value: [today().subtract(1, 'day'), today().subtract(1, 'day')] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.last7'), value: [today().subtract(6, 'day'), today()] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.last30'), value: [today().subtract(29, 'day'), today()] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.thisMonth'), value: [today().startOf('month'), today()] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.prevMonth'), value: [today().subtract(1, 'month').startOf('month'), today().subtract(1, 'month').endOf('month')] as [Dayjs, Dayjs] },
]

const deliveryPresets = () => [
    { label: i.t('orders:filters.presets.today'), value: [today(), today()] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.tomorrow'), value: [today().add(1, 'day'), today().add(1, 'day')] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.dayAfterTomorrow'), value: [today().add(2, 'day'), today().add(2, 'day')] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.next7'), value: [today(), today().add(6, 'day')] as [Dayjs, Dayjs] },
    { label: i.t('orders:filters.presets.yesterday'), value: [today().subtract(1, 'day'), today().subtract(1, 'day')] as [Dayjs, Dayjs] },
]

// Пустой диапазон — null, иначе пара границ (одна из них может быть не задана)
const rangeValue = (from: Dayjs | null, to: Dayjs | null): [Dayjs | null, Dayjs | null] | null => from || to ? [from, to] : null

const sameDay = (a: Dayjs | null, b: Dayjs) => !!a && a.isSame(b, 'day')

// Быстрые фильтры: повторное нажатие снимает
const QUICK_FILTERS: { key: string, active: (f: any) => boolean, patch: (on: boolean) => Record<string, any> }[] = [
    {
        key: 'deliveryToday',
        active: f => sameDay(f.delivery_date_from, today()) && sameDay(f.delivery_date_to, today()),
        patch: on => on ? { delivery_date_from: today(), delivery_date_to: today() } : { delivery_date_from: null, delivery_date_to: null },
    },
    {
        key: 'deliveryTomorrow',
        active: f => sameDay(f.delivery_date_from, today().add(1, 'day')) && sameDay(f.delivery_date_to, today().add(1, 'day')),
        patch: on => on ? { delivery_date_from: today().add(1, 'day'), delivery_date_to: today().add(1, 'day') } : { delivery_date_from: null, delivery_date_to: null },
    },
    {
        key: 'createdToday',
        active: f => sameDay(f.created_at_from, today()) && sameDay(f.created_at_to, today()),
        patch: on => on ? { created_at_from: today(), created_at_to: today() } : { created_at_from: null, created_at_to: null },
    },
    {
        key: 'unpaid',
        active: f => f.payment_status.length === 1 && Number(f.payment_status[0]) === UNPAID,
        patch: on => ({ payment_status: on ? [UNPAID] : [] }),
    },
]

const OrdersToolbar = observer(() => {
    const filters = __order.filterOrders.filters
    const fromPush = !!filters.push_id

    const isFranchisee = storeLocal.get('role') === 6
    const allowedWarehouseIds: number[] = storeLocal.get('warehouses_access') || []
    const warehouseOptions = (isFranchisee
        ? warehouses.list.filter((el: any) => allowedWarehouseIds.includes(el.id))
        : warehouses.list
    ).map((el: any) => ({ label: el.name, value: el.id }))
    const zoneOptions = warehouses.zones.map((el: any) => ({ label: el.name, value: el.id }))

    const toggleStatus = (id: number) => __order.setFilters({
        status: filters.status.includes(id) ? filters.status.filter((el: number) => el !== id) : [...filters.status, id]
    })

    return (
        <ListToolbar>
            <ListToolbarField>
                <InputNumber
                    className="ol-input-number"
                    prefix={<NumberOutlined />}
                    placeholder={i.t('orders:filters.number')}
                    min={1}
                    precision={0}
                    controls={false}
                    value={filters.id}
                    onChange={value => __order.setFilters({ id: value || null }, true)}
                />
            </ListToolbarField>
            <ListToolbarField>
                <Input
                    allowClear
                    prefix={<PhoneOutlined />}
                    placeholder={i.t('orders:filters.phone')}
                    value={filters.client_phone}
                    onChange={e => __order.setFilters({ client_phone: e.target.value }, true)}
                />
            </ListToolbarField>
            <ListToolbarField>
                <Input
                    allowClear
                    prefix={<MailOutlined />}
                    placeholder={i.t('orders:filters.email')}
                    value={filters.client_email}
                    onChange={e => __order.setFilters({ client_email: e.target.value }, true)}
                />
            </ListToolbarField>
            <ListToolbarSpacer />
            {!fromPush &&
                <Segmented
                    value={__order.pagination.is_archive ? 'archive' : 'active'}
                    options={[
                        { label: i.t('orders:filters.active'), value: 'active' },
                        { label: i.t('orders:filters.archive'), value: 'archive' },
                    ]}
                    onChange={value => __order.setArchiveTab(value === 'archive')}
                />
            }

            <ListToolbarChips label={i.t('orders:filters.quick')}>
                {QUICK_FILTERS.map(({ key, active, patch }) => {
                    const on = active(filters)
                    return <ListChip key={key} active={on} onClick={() => __order.setFilters(patch(!on))}>{i.t(`orders:filters.${key}`)}</ListChip>
                })}
            </ListToolbarChips>

            <ListToolbarBreak
                title={i.t('orders:filters.title')}
                open={__order.filtersOpen}
                onToggle={() => __order.toggleFilters()}
                count={__order.panelFiltersCount}
                extra={__order.filtersActive &&
                    <Button size="small" type="link" onClick={() => __order.filterClear()}>{i.t('orders:filters.reset')}</Button>
                }
            />

            {__order.filtersOpen && <>
                <ListToolbarField label={i.t('orders:filters.delivery')} hint={i.t('orders:filters.deliveryHint')}>
                    <DatePicker.RangePicker
                        className="ol-range"
                        format="DD.MM.YYYY"
                        presets={deliveryPresets()}
                        placeholder={[i.t('orders:filters.rangeStart'), i.t('orders:filters.rangeEnd')]}
                        value={rangeValue(filters.delivery_date_from, filters.delivery_date_to)}
                        allowEmpty={[true, true]}
                        onChange={(dates: any) => __order.setFilters({ delivery_date_from: dates?.[0] ?? null, delivery_date_to: dates?.[1] ?? null })}
                    />
                </ListToolbarField>
                <ListToolbarField label={i.t('orders:filters.created')} hint={i.t('orders:filters.createdHint')}>
                    <DatePicker.RangePicker
                        className="ol-range"
                        format="DD.MM.YYYY"
                        presets={createdPresets()}
                        placeholder={[i.t('orders:filters.rangeStart'), i.t('orders:filters.rangeEnd')]}
                        value={rangeValue(filters.created_at_from, filters.created_at_to)}
                        allowEmpty={[true, true]}
                        onChange={(dates: any) => __order.setFilters({ created_at_from: dates?.[0] ?? null, created_at_to: dates?.[1] ?? null })}
                    />
                </ListToolbarField>
                <ListToolbarField label={i.t('orders:filters.payment')}>
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        style={{ width: '100%' }}
                        placeholder={i.t('orders:filters.any')}
                        options={payments.list.map((el: any) => ({ label: el.name, value: el.id }))}
                        value={filters.payment_status}
                        onChange={value => __order.setFilters({ payment_status: value ?? [] })}
                    />
                </ListToolbarField>
                {warehouseOptions.length > 0 &&
                    <ListToolbarField label={i.t('orders:filters.warehouse')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            maxTagCount="responsive"
                            style={{ width: '100%' }}
                            placeholder={i.t('orders:filters.any')}
                            options={warehouseOptions}
                            value={filters.warehouses}
                            onChange={value => __order.setFilters({ warehouses: value ?? [] })}
                        />
                    </ListToolbarField>
                }
                {zoneOptions.length > 0 &&
                    <ListToolbarField label={i.t('orders:filters.zone')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            maxTagCount="responsive"
                            style={{ width: '100%' }}
                            placeholder={i.t('orders:filters.any')}
                            options={zoneOptions}
                            value={filters.delivery_zones}
                            onChange={value => __order.setFilters({ delivery_zones: value ?? [] })}
                        />
                    </ListToolbarField>
                }
                <ListToolbarField label={i.t('orders:filters.source')}>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('orders:filters.any')}
                        options={[
                            { label: 'iOS', value: 'ios' },
                            { label: 'Android', value: 'android' },
                            { label: i.t('order:site'), value: 'web' },
                        ]}
                        value={filters.source ?? undefined}
                        onChange={value => __order.setFilters({ source: value ?? null })}
                    />
                </ListToolbarField>
                <ListToolbarField label={i.t('orders:filters.receiving')}>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('orders:filters.any')}
                        options={[
                            { label: i.t('order:deliveries'), value: 'delivery' },
                            { label: i.t('order:pickup'), value: 'pickup' },
                            { label: i.t('order:tk'), value: 'tk' },
                        ]}
                        value={filters.delivery_type ?? undefined}
                        onChange={value => __order.setFilters({ delivery_type: value ?? null })}
                    />
                </ListToolbarField>
                <ListToolbarField label={i.t('orders:filters.recipientPhone')}>
                    <Input
                        allowClear
                        prefix={<PhoneOutlined />}
                        placeholder={i.t('orders:filters.recipientPhonePlaceholder')}
                        value={filters.recipient_phone}
                        onChange={e => __order.setFilters({ recipient_phone: e.target.value }, true)}
                    />
                </ListToolbarField>
                <ListToolbarField label={i.t('orders:filters.clientId')}>
                    <InputNumber
                        className="ol-input-number"
                        prefix={<UserOutlined />}
                        placeholder={i.t('orders:filters.clientIdPlaceholder')}
                        min={1}
                        precision={0}
                        controls={false}
                        value={filters.user_id}
                        onChange={value => __order.setFilters({ user_id: value || null }, true)}
                    />
                </ListToolbarField>

                {__order.statuses.length > 0 &&
                    <ListToolbarChips label={i.t('orders:filters.status')}>
                        {__order.statuses.map((status: any) => (
                            <ListChip
                                key={status.id}
                                active={filters.status.includes(status.id)}
                                icon={<StatusDot color={statusColor(status)} />}
                                onClick={() => toggleStatus(status.id)}
                            >
                                {status.name}
                            </ListChip>
                        ))}
                    </ListToolbarChips>
                }
            </>}
        </ListToolbar>
    )
})

export const OrdersList = observer(() => {

    useEffect(() => {
        __order.readFiltersFromUrl()
        __order.getList()
        payments.getList()
        __order.getStatuses()
        warehouses.pagination.limit = 9999999
        warehouses.get()
        warehouses.getZones()
        __multiple.setActions(__order.getActions())

        // Фильтры — в адресе страницы, чтобы ссылкой можно было поделиться
        return reaction(
            () => __order.filtersUrl,
            url => window.history.replaceState(null, '', url),
        )
    }, [])

    const buttons: any[] = [
        { title: i.t('def:flag_read_all'), click: () => __order.viewed(), type: 'dashed' },
        { title: i.t('def:Create Order'), href: '/shop/orders/0', type: 'primary' },
    ]

    const filters = __order.filterOrders.filters
    const list: any[] = __order.list
    const pageSelected = list.filter(order => __multiple.selected.includes(order.id)).length
    const filtered = __order.filtersActive || !!filters.push_id

    return (
        <>
            <Titles
                title={i.t('orders:orders')}
                buttons={buttons}
                breadcrumb={[
                    { title: i.t('order:orders'), href: '/shop/orders' }
                ]}
            />

            <div className="ol-page">
                {filters.push_id &&
                    // Пришли из воронки пушей: заказы рассылки, и активные, и архивные — вкладки скрыты
                    <Alert
                        type="info"
                        showIcon
                        message={i.t('orders:fromPush', { id: filters.push_id })}
                        description={i.t('orders:fromPushHint')}
                        action={<Button size="small" onClick={() => __order.clearPushFilter()}>{i.t('orders:fromPushReset')}</Button>}
                    />
                }

                <OrdersSummary />

                <div className={`ol-list${__order.listLoading ? ' ol-list--loading' : ''}`}>
                    <CardList
                        title={filters.push_id
                            ? i.t('orders:list.titlePush')
                            : __order.pagination.is_archive ? i.t('orders:list.titleArchive') : i.t('orders:list.title')
                        }
                        count={__order.pagination.total}
                        items={list}
                        loading={!__order.isLoading.list || __order.listLoading}
                        toolbar={<OrdersToolbar />}
                        extra={list.length > 0 &&
                            <div className="ol-head-extra">
                                <Segmented
                                    size="small"
                                    value={__order.sort}
                                    options={[
                                        { label: i.t('orders:list.sortCreated'), value: 'created' },
                                        { label: i.t('orders:list.sortDelivery'), value: 'delivery_date' },
                                    ]}
                                    onChange={value => __order.setSort(value as OrdersSort)}
                                />
                                <Checkbox
                                    checked={pageSelected > 0 && pageSelected === list.length}
                                    indeterminate={pageSelected > 0 && pageSelected < list.length}
                                    onChange={e => __multiple.set(e.target.checked ? list : [])}
                                >
                                    {i.t('orders:list.selectPage')}
                                </Checkbox>
                            </div>
                        }
                        renderItem={(order: any) => <OrderCard order={order} />}
                        empty={filtered
                            ? {
                                icon: <SearchOutlined />,
                                title: i.t('orders:list.empty'),
                                text: i.t('orders:list.emptyHint'),
                                action: __order.filtersActive
                                    ? <Button onClick={() => __order.filterClear()}>{i.t('orders:filters.reset')}</Button>
                                    : undefined,
                            }
                            : __order.pagination.is_archive
                                ? { icon: <InboxOutlined />, title: i.t('orders:list.emptyArchive'), text: i.t('orders:list.emptyArchiveHint') }
                                : {
                                    icon: <ShoppingOutlined />,
                                    title: i.t('orders:list.emptyAll'),
                                    text: i.t('orders:list.emptyAllHint'),
                                    action: <Button type="primary" icon={<PlusOutlined />} href="/shop/orders/0">{i.t('def:Create Order')}</Button>,
                                }
                        }
                        pagination={{
                            current: __order.pagination.page,
                            pageSize: __order.pagination.limit,
                            total: __order.pagination.total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                __order.setPageSize(page, pageSize)
                                window.scrollTo({ top: 0, behavior: 'smooth' })
                            }
                        }}
                    />
                </div>
            </div>
        </>
    )
})
